import { cn } from '@app/utils/cn';
import { isDefined } from '@app/utils/is';
import React from 'react';

import { Check } from '../Icons';
import { TextEllipsis } from '../TextEllipsis';

import { MenuItemCheckbox } from './MenuItemCheckbox';

interface DefaultProps {
  children: React.ReactNode;
  isSelected?: boolean;
  isFocused?: boolean;
  postfix?: React.ReactNode;
  prefix?: React.ReactNode;
  description?: React.ReactNode;
  isDisabled?: boolean;
  hasDivider?: boolean;
  size?: 'large' | 'medium';
  role: 'menuitem' | 'menuitemradio' | 'menuitemcheckbox' | 'option';
  onClick?: () => void;
  maskData?: boolean;
}

interface ListItemProps extends DefaultProps {
  variant?: 'list';
}

interface CheckboxItemProps extends DefaultProps {
  variant: 'checkbox';
  isIndeterminate?: boolean;
}

type Props<T extends React.ElementType> = (ListItemProps | CheckboxItemProps) & {
  as?: T;
} & Omit<React.ComponentPropsWithRef<T>, keyof DefaultProps>;

export const MenuItem = React.forwardRef(<T extends React.ElementType>(props: Props<T>, ref: React.ComponentPropsWithRef<T>['ref']) => {
  const {
    as: Component = 'button',
    children,
    className,
    description,
    isDisabled,
    isFocused,
    isIndeterminate = false,
    isSelected,
    postfix,
    prefix,
    size = 'large',
    role,
    variant = 'list',
    hasDivider = false,
    maskData,
    ...rest
  } = props;

  const isListVariant = variant === 'list';
  const isCheckboxVariant = variant === 'checkbox';
  const isMediumSize = size === 'medium';

  const buttonProps = Component === 'button' ? { disabled: isDisabled, type: 'button' as const } : {};

  const typographyClassName = React.useMemo(() => {
    if (isListVariant) {
      if (isMediumSize) {
        return isSelected ? 'body-4' : 'body-5';
      }

      return isSelected ? 'body-1' : 'body-2';
    }

    return isMediumSize ? 'body-5' : 'body-2';
  }, [isListVariant, isMediumSize, isSelected]);

  const sentryDataMaskAttribute = maskData ? { 'data-sentry-mask': 'true' } : {};

  return (
    <>
      {hasDivider && <div className="h-px w-full bg-neutral-90" />}
      <Component
        {...rest}
        {...buttonProps}
        ref={ref}
        role={role}
        aria-selected={isSelected}
        aria-disabled={isDisabled}
        className={cn(
          'flex h-10 w-full items-center rounded-regular px-2 py-1 text-left text-neutral-20 transition-colors focus-visible:focus-outline sm:h-auto',
          {
            'hover:bg-neutral-96 focus-visible:bg-neutral-96': isListVariant && !isDisabled,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            'bg-neutral-96 hover:bg-neutral-90 focus-visible:bg-neutral-90': isListVariant && isSelected && !isDisabled,
            'hover:text-neutral-40': isCheckboxVariant && !isDisabled,
            // eslint-disable-next-line sort-keys-fix/sort-keys-fix
            'cursor-not-allowed text-neutral-60': isDisabled,
          },
          className,
        )}
        tabIndex={isFocused ? 0 : -1}
      >
        <span
          {...sentryDataMaskAttribute}
          className={cn('group flex w-full items-center gap-2 py-0.5', typographyClassName, { 'py-px': isMediumSize })}
        >
          {isCheckboxVariant && <MenuItemCheckbox isSelected={isSelected} isIndeterminate={isIndeterminate} isDisabled={isDisabled} />}
          {isDefined(prefix) && (
            <span className={cn('text-neutral-40', { 'text-neutral-20': isSelected, 'text-neutral-60': isDisabled })}>{prefix}</span>
          )}
          <TextEllipsis className="flex-1">
            {children}
            {isDefined(description) && <span className="ml-2 text-neutral-60">({description})</span>}
          </TextEllipsis>
          {isListVariant && isSelected && <Check className={cn('size-4', { 'h-3 w-3': isMediumSize })} />}
          {isDefined(postfix) && <span>{postfix}</span>}
        </span>
      </Component>
    </>
  );
}) as <T extends React.ElementType = 'button'>(props: Props<T>) => JSX.Element;
